import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { invoiceActions } from '../../../sagas/invoiceSlice';
import './InvoiceList.css';
import { clientActions } from '../../../sagas/clientSlice';
import {
    Badge,
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Modal,
    ModalBody,
    ModalHeader,
    Pagination,
    PaginationItem,
    PaginationLink,
    Popover,
    PopoverBody,
    Row,
    Spinner,
    Input,
    FormGroup,
    Label,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem
} from 'reactstrap';
import {
    FaFileInvoice,
    FaFilePdf,
    FaSearch,
    FaMoneyBillWave,
    FaClock,
    FaPlus,
    FaCopy,
    FaCheck,
    FaTimes,
    FaEdit,
    FaEye,
    FaTrash,
    FaEllipsisV,
    FaCheckCircle,
    FaExclamationTriangle,
    FaTimesCircle,
    FaFilter
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { debounce } from 'lodash';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatPrice, convertNumberToWords } from '../../../helpers/utility';
import SubInvoiceManager from '../SubInvoiceManager';
import { productActions } from '../../../sagas/productSlice';
import { companyActions } from '../../../sagas/companySlice';
import InvoiceForm from '../InvoiceForm';
import ViewFacture from '../ViewFacture';
import { generateDoc } from '../../../helpers/generateDoc';
import Select from "react-select";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { FaFileExcel } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import useSessionState from '../../../hooks/useSessionState';

const InvoiceList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { invoices, loading, subInvoices } = useSelector((state) => state.invoices);
    const { t } = useTranslation();
    const { company } = useSelector(state => state.auth);
    const { companies } = useSelector((state) => state.companies);
    const defaultCompanyId = useMemo(
        () => companies?.find((item) => item.isDefault)?.id ?? null,
        [companies]
    );
    const searchInputRef = useRef(null);

    // États pour la gestion des sous-factures
    const [showSubInvoiceModal, setShowSubInvoiceModal] = useState(false);
    const [selectedInvoiceForSubInvoices, setSelectedInvoiceForSubInvoices] = useState(null);
    const now = new Date();
    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const monthStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const tomorrowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const pad = (n) => n.toString().padStart(2, '0');
    const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const getMonthDateRange = (monthValue) => {
        if (!monthValue) {
            return { dateFrom: '', dateTo: '' };
        }

        const [year, month] = monthValue.split('-').map(Number);
        const firstDay = new Date(year, month - 1, 1);
        const lastDay = new Date(year, month, 0);

        return {
            dateFrom: formatDate(firstDay),
            dateTo: formatDate(lastDay),
        };
    };
    const [showInvoiceModal, setShowInvoiceModal] = useState(false); // State to control the invoice modal
    const [invoice, setInvoice] = useState({
        lignes: [],
        dateFacture: new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
        dateEcheance: new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
        statut: 'IMPAYE'
    }); // State to hold the invoice object
    const clients = useSelector(state => state.clients.clients);

    // États pour l'édition des factures
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedInvoiceForEdit, setSelectedInvoiceForEdit] = useState(null);
    const [filters, setFilters] = useSessionState('clientInvoiceList.filters', {
        clientId: null,
        companyId: null,
        dateFrom: formatDate(monthStartDate),
        dateTo: formatDate(tomorrowDate),
        query: '',
        amount: '',
        status: '',
        month: ''
    });
    const [sortConfig, setSortConfig] = useSessionState('clientInvoiceList.sortConfig', { key: null, direction: 'asc' });
    const [currentPage, setCurrentPage] = useSessionState('clientInvoiceList.currentPage', 1);
    const [rowsPerPage, setRowsPerPage] = useSessionState('clientInvoiceList.rowsPerPage', 10);
    const didRestoreListState = useRef(false);

    const accuseOptions = [
        { value: -1, label: t('common.all', 'Tous') },
        { value: 0, label: t('delivery.nonAccuse', 'Non accusé') },
        { value: 1, label: t('delivery.accuse', 'Accusé') },
    ];

    useEffect(() => {
        // Auto focus on mount
        searchInputRef.current?.focus();

        // Add keyboard shortcut
        const handleKeyDown = (e) => {
            if (e.key === 'F2') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);


    useEffect(() => {
        dispatch(productActions.findAllUnites());
        dispatch(clientActions.search({ query: "" }));
        dispatch(companyActions.getAll({}));
    }, []);

    useEffect(() => {
        if (defaultCompanyId && filters.companyId == null) {
            setFilters((prev) => ({ ...prev, companyId: defaultCompanyId }));
        }
    }, [defaultCompanyId]);

    useEffect(() => {
        dispatch(invoiceActions.listWithSubInvoices({
            ...filters,
        }));
    }, [filters.clientId, filters.companyId, filters.dateFrom, filters.dateTo, filters.amount, filters.status]);

    const debouncedSearch = () => {
        dispatch(invoiceActions.listWithSubInvoices({
            ...filters,
        }));
    }

    const handleExportInvoicesExcel = () => {
        if (!invoices || invoices.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: t('common.warning') || 'Attention',
                text: t('facture.noInvoices') || 'Aucune facture à exporter'
            });
            return;
        }

        // Prepare data
        const data = invoices.map(inv => ({
            "N° Facture": inv.numero || '',
            "Client": inv.clientNom || '',
            "Entreprise": getInvoiceCompanyName(inv),
            "Date facture": inv.dateFacture || '',
            "Statut": inv.statut || '',
            "Total HT": inv.montantTotalHT || 0,
            "TVA": inv.montantTotalTaxe || 0,
            "Total TTC": inv.montantTotalTTC || 0,
            "Montant réglé": inv.montantRegle || 0,
            "BL associés": inv.bonLivraionsRefs || ''
        }));

        // Create worksheet
        const worksheet = XLSX.utils.json_to_sheet(data);

        // Auto width columns
        const colWidths = Object.keys(data[0]).map(key => ({
            wch: Math.max(
                key.length,
                ...data.map(row => String(row[key] ?? '').length)
            )
        }));
        worksheet['!cols'] = colWidths;

        // Create workbook
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Factures');

        // Export
        const excelBuffer = XLSX.write(workbook, {
            bookType: 'xlsx',
            type: 'array'
        });

        const blob = new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const filename = `Factures_${filters.dateFrom || ''}_${filters.dateTo || ''}.xlsx`;
        saveAs(blob, filename);
    };



    const clientOptions = clients?.map(client => ({
        value: client.id,
        label: client.typeClient === 'PARTICULIER'
            ? `${client.nom} ${client.prenom || ''} `
            : client.entreprise,
        chantiers: client.chantiers || []
    })) ?? [];

    const clientOptionsWithAll = [
        { value: null, label: t('common.all', 'Tous') },
        ...clientOptions
    ];

    const companyOptions = companies?.map(item => ({
        value: item.id,
        label: item.name,
    })) ?? [];

    const companyOptionsWithAll = [
        { value: null, label: t('common.all', 'Tous') },
        ...companyOptions
    ];
    const compactSelectStyles = {
        control: (base) => ({
            ...base,
            minHeight: '31px',
            fontSize: '0.875rem',
        }),
        valueContainer: (base) => ({
            ...base,
            padding: '0 8px',
        }),
        input: (base) => ({
            ...base,
            margin: 0,
            padding: 0,
        }),
        indicatorsContainer: (base) => ({
            ...base,
            height: '31px',
        }),
        clearIndicator: (base) => ({
            ...base,
            padding: '4px',
        }),
        dropdownIndicator: (base) => ({
            ...base,
            padding: '4px',
        }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    };

    // Fonction pour ouvrir le modal d'édition
    const handleEditInvoice = (invoice) => {
        setSelectedInvoiceForEdit(invoice);
        setShowEditModal(true);
    };

    const getInvoiceRemainingAmount = (invoice) =>
        Math.max(Number(invoice?.montantTotalTTC || invoice?.montantTTC || 0) - Number(invoice?.montantRegle || invoice?.montantPaye || 0), 0);

    const isInvoicePaid = (invoice) =>
        invoice?.statut === 'PAYEE' || (
            Number(invoice?.montantTotalTTC || invoice?.montantTTC || 0) > 0 &&
            getInvoiceRemainingAmount(invoice) <= 0
        );

    const handleCreatePaymentFromInvoice = (invoice) => {
        const remainingAmount = getInvoiceRemainingAmount(invoice);

        if (remainingAmount <= 0) {
            Swal.fire({
                icon: 'info',
                title: t('common.info', 'Information'),
                text: t('reglement.noAmountToPay', 'Aucun montant restant a regler.'),
            });
            return;
        }

        navigate('/sales-module/payments/new', {
            state: {
                quickPayment: {
                    typeReglement: 'FACTURE',
                    clientId: invoice.clientId,
                    chantierId: invoice.chantierId || null,
                    companyId: invoice.companyId || invoice.company?.id || null,
                    montantRegle: remainingAmount,
                    documents: [invoice],
                },
            },
        });
    };

    // Fonction pour fermer le modal d'édition
    const handleCloseEditModal = () => {
        dispatch(invoiceActions.listWithSubInvoices({
            ...filters,
        }));
        setShowEditModal(false);
        setSelectedInvoiceForEdit(null);
    };

    // Fonction pour sauvegarder les modifications
    const handleSaveInvoice = () => {
        dispatch(invoiceActions.listWithSubInvoices({
            ...filters,
        }));

        handleCloseEditModal();
    };

    // Créer les sous-factures
    const handleSubInvoicesCreation = (newSubInvoices) => {
        const payload = {
            parentInvoiceId: selectedInvoiceForSubInvoices.id,
            subInvoices: newSubInvoices.map(sub => ({
                numero: sub.numero,
                montantTTC: sub.montant,
                description: sub.description,
                clientId: selectedInvoiceForSubInvoices.clientId,
                lignes: calculateSubInvoiceLines(selectedInvoiceForSubInvoices, sub.montant)
            }))
        };

        dispatch(invoiceActions.createSubInvoices({
            ...payload,
            onSuccess: (createdSubInvoices) => {
                Swal.fire({
                    icon: 'success',
                    title: t('subInvoice.created'),
                    text: t('subInvoice.createdMessage', { count: newSubInvoices.length }),
                    showConfirmButton: false,
                    timer: 2000
                });

                setShowSubInvoiceModal(false);
                setSelectedInvoiceForSubInvoices(null);
            },
            onError: (error) => {
                Swal.fire({
                    icon: 'error',
                    title: t('subInvoice.creationError'),
                    text: error.message || t('subInvoice.creationErrorMessage')
                });
            }
        }));
    };

    // Calculer les lignes pour une sous-facture (proportionnellement)
    const calculateSubInvoiceLines = (parentInvoice, subInvoiceAmount) => {
        const ratio = subInvoiceAmount / parentInvoice.montantTotalTTC;

        return parentInvoice.lignes.map(ligne => ({
            ...ligne,
            quantite: ligne.quantite * ratio,
            montantHT: ligne.montantHT * ratio,
            montantTTC: ligne.montantTTC * ratio,
            montantTaxe: ligne.montantTaxe * ratio
        }));
    };

    // Obtenir les sous-factures d'une facture
    const getSubInvoicesForInvoice = (invoiceId) => {
        return subInvoices[invoiceId] || [];
    };

    // Fonction pour déterminer si une facture peut être éditée
    const canEditInvoice = (invoice) => {
        const invoiceSubInvoices = getSubInvoicesForInvoice(invoice.id);
        return invoice.statut !== 'PAYEE' &&
            invoice.statut !== 'ANNULEE' &&
            invoiceSubInvoices.length === 0;
    };

    // Fonction pour déterminer si une facture peut être supprimée
    const canDeleteInvoice = (invoice) => {
        const invoiceSubInvoices = getSubInvoicesForInvoice(invoice.id);
        return invoice.statut !== 'PAYEE' &&
            invoiceSubInvoices.length === 0;
    };

    const getInvoiceCompanyName = (invoice) => {
        if (invoice?.companyName) return invoice.companyName;
        if (invoice?.company?.name) return invoice.company.name;
        return companies?.find(item => item.id === invoice?.companyId)?.name || '-';
    };

    const getInvoiceCompanyModel = (invoice) => {
        if (invoice?.company?.name) {
            return invoice.company;
        }

        const matchedCompany = companies?.find(item => item.id === invoice?.companyId);
        return matchedCompany || company;
    };

    const getSelectedCompanyModel = () => {
        if (filters.companyId) {
            return companies?.find(item => Number(item.id) === Number(filters.companyId)) || company;
        }

        return company || companies?.find(item => item.isDefault) || companies?.[0] || null;
    };

    const getCompanyValue = (model, keys, fallback = '') => {
        const keyList = Array.isArray(keys) ? keys : [keys];
        const foundKey = keyList.find(key => model?.[key]);
        return foundKey ? model[foundKey] : fallback;
    };

    const getPaymentModeLabel = (value) => {
        const mode = String(value || '').trim().toUpperCase();
        const labels = {
            ESPECES: t('facture.modeReglement.espece', 'Especes'),
            ESPECE: t('facture.modeReglement.espece', 'Especes'),
            CASH: t('facture.modeReglement.espece', 'Especes'),
            CHEQUE: t('facture.modeReglement.cheque', 'Cheque'),
            VIREMENT: t('facture.modeReglement.virement', 'Virement'),
            CARTE_BANCAIRE: t('facture.modeReglement.card', 'Carte bancaire'),
            CARD: t('facture.modeReglement.card', 'Carte bancaire'),
            TRAITE: t('facture.modeReglement.traite', 'Traite'),
        };

        return labels[mode] || value || '-';
    };

    const getSortValue = (invoice, key) => {
        switch (key) {
            case 'numero':
                return invoice.numero || '';
            case 'clientNom':
                return invoice.clientNom || '';
            case 'companyName':
                return getInvoiceCompanyName(invoice);
            case 'dateFacture':
                return invoice.dateFacture ? new Date(invoice.dateFacture).getTime() || 0 : 0;
            case 'createdBy':
                return invoice.createdBy || '';
            case 'montantTotalTTC':
                return Number(invoice.montantTotalTTC || 0);
            case 'montantTotalHT':
                return Number(invoice.montantTotalHT || 0);
            case 'montantTotalTaxe':
                return Number(invoice.montantTotalTaxe || 0);
            case 'montantRegle':
                return Number(invoice.montantRegle || 0);
            default:
                return '';
        }
    };

    const sortedInvoices = useMemo(() => {
        const rows = [...(invoices || [])];
        if (!sortConfig.key) return rows;

        rows.sort((a, b) => {
            const left = getSortValue(a, sortConfig.key);
            const right = getSortValue(b, sortConfig.key);

            if (typeof left === 'number' && typeof right === 'number') {
                return sortConfig.direction === 'asc' ? left - right : right - left;
            }

            return sortConfig.direction === 'asc'
                ? String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' })
                : String(right).localeCompare(String(left), undefined, { numeric: true, sensitivity: 'base' });
        });

        return rows;
    }, [invoices, sortConfig]);

    const totalPages = Math.max(1, Math.ceil(sortedInvoices.length / rowsPerPage));
    const currentPageSafe = Math.min(currentPage, totalPages);
    const paginatedInvoices = sortedInvoices.slice((currentPageSafe - 1) * rowsPerPage, currentPageSafe * rowsPerPage);
    const pageStart = sortedInvoices.length === 0 ? 0 : (currentPageSafe - 1) * rowsPerPage + 1;
    const pageEnd = Math.min(currentPageSafe * rowsPerPage, sortedInvoices.length);

    useEffect(() => {
        if (!didRestoreListState.current) {
            didRestoreListState.current = true;
            return;
        }
        setCurrentPage(1);
    }, [filters.clientId, filters.companyId, filters.dateFrom, filters.dateTo, filters.amount, filters.status, filters.query, filters.month, rowsPerPage]);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    const renderSortableHeader = (label, key) => (
        <th
            className="align-middle text-center"
            onClick={() => handleSort(key)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
        >
            {label}{sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}
        </th>
    );

    // Fonction pour générer le PDF
    const handleGeneratePdf = (invoice) => {
        Swal.fire({
            title: t('common.loading'),
            text: t('facture.pdfLoading', 'Chargement des reglements...'),
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        const generateInvoicePdf = (reglements = []) => {
            Swal.close();
            generateDoc("facture", {
                matjarDoc: invoice,
                sellerModel: getInvoiceCompanyModel(invoice),
                priceMode: "HT",
                open: true,
                reglements,
            });
        };

        const onError = () => {
            Swal.fire({
                icon: 'error',
                title: t('common.error'),
                text: t('facture.pdfError', 'Erreur lors de la generation du PDF.'),
            });
        };

        if (invoice?.paymentsRefs) {
            dispatch(invoiceActions.fetchInvoiceReglementsByRefs({
                paymentsRefs: invoice.paymentsRefs,
                onSuccess: generateInvoicePdf,
                onError,
            }));
            return;
        }

        dispatch(invoiceActions.fetchInvoiceReglements({
            numero: invoice.numero,
            onSuccess: (reglements = []) => {
                generateInvoicePdf(reglements);
            },
            onError,
        }));
    };

    // Fonction pour obtenir le badge de statut
    const getStatusBadge = (statut) => {
        switch (statut) {
            case 'BROUILLON':
                return <Badge color="secondary"><FaClock className="me-1" />{t('facture.status.draft')}</Badge>;
            case 'IMPAYE':
                return <Badge color="secondary"><FaClock className="me-1" />{t('facture.unpaid')}</Badge>;
            case 'VALIDEE':
                return <Badge color="info"><FaCheckCircle className="me-1" />{t('facture.validated')}</Badge>;
            case 'PAYEE':
                return <Badge color="success"><FaCheck className="me-1" />{t('facture.paid')}</Badge>;
            case 'PARTIELLEMENT_PAYEE':
                return <Badge color="warning"><FaExclamationTriangle className="me-1" />{t('facture.partiallyPaid')}</Badge>;
            case 'ANNULEE':
                return <Badge color="danger"><FaTimesCircle className="me-1" />{t('facture.cancelled')}</Badge>;
            case 'EN_RETARD':
                return <Badge color="danger"><FaClock className="me-1" />{t('facture.status.overdue')}</Badge>;
            default:
                return <Badge color="light">{statut}</Badge>;
        }
    };

    const clearFilters = () => {
        setFilters({
            query: '',
            clientId: null,
            companyId: defaultCompanyId,
            dateFrom: formatDate(monthStartDate),
            dateTo: formatDate(tomorrowDate),
            amount: '',
            status: '',
            month: ''
        });
    };

    const handleCloseInvoiceModal = () => {
        setShowInvoiceModal(false);
        setInvoice({
            lignes: [],
            dateFacture: new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
            dateEcheance: new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
            statut: 'IMPAYE'
        });
        dispatch(invoiceActions.listWithSubInvoices({
            ...filters,
        }));
    };

    // Handle creating an invoice from selected deliveries
    const handleCreateInvoice = () => {
        setShowInvoiceModal(true);
    };

    const handleDownloadListPdfLegacy = () => {
        try {
            const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
            const pageWidth = doc.internal.pageSize.getWidth();
            const margin = 14;

            // --------- Header
            const title = t('facture.list') || 'Liste des factures';
            const period =
                `${t('common.from') || 'Du'} ${filters.dateFrom || '-'} ` +
                `${t('common.to') || 'au'} ${filters.dateTo || '-'}`;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(16);
            doc.text(title, margin, 18);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.text(period, margin, 26);

            // Company (if available)
            if (company?.name) {
                const right = pageWidth - margin;
                doc.setFontSize(9);
                doc.text(company.name, right, 12, { align: 'right' });
                if (company.address) doc.text(company.address, right, 16, { align: 'right' });
                if (company.email || company.phone1) {
                    doc.text(
                        `${company.email ? company.email : ''}${company.email && company.phone1 ? ' | ' : ''}${company.phone1 || ''}`,
                        right, 20, { align: 'right' }
                    );
                }
            }

            // --------- Table data
            const tableBody = (invoices || []).map(inv => ([
                inv.numero || '',
                inv.clientNom || '',
                getInvoiceCompanyName(inv),
                inv.dateFacture || '',
                inv.statut || '',
                formatPrice(inv.montantTotalTTC || 0, false),
                formatPrice(inv.montantTotalHT || 0, false),
                formatPrice(inv.montantTotalTaxe || 0, false),
            ]));

            autoTable(doc, {
                startY: 32,
                head: [[
                    t('facture.table.invoiceNb') || 'N° Facture',
                    t('facture.client') || 'Client',
                    t('company.company', 'Entreprise'),
                    t('facture.table.Date') || 'Date',
                    t('facture.table.status') || 'Statut',
                    t('facture.table.totalTtc') || 'Total TTC',
                    t('facture.table.totalHt') || 'Total HT',
                    t('reception.totalTva') || 'TVA'
                ]],
                body: tableBody,
                styles: { fontSize: 9, cellPadding: 3 },
                headStyles: {
                    fillColor: [52, 73, 94],
                    textColor: 255,
                    fontStyle: 'bold',
                    valign: 'middle', // vertical centering
                },
                columnStyles: {
                    0: { cellWidth: 45, halign: 'center', headerHalign: 'center' },  // Invoice #
                    1: { cellWidth: 55, halign: 'left', headerHalign: 'left' },    // Client
                    2: { cellWidth: 45, halign: 'left', headerHalign: 'left' },    // Company
                    3: { cellWidth: 26, halign: 'center', headerHalign: 'center' },  // Date
                    4: { cellWidth: 30, halign: 'center', headerHalign: 'center' },  // Status
                    5: { cellWidth: 30, halign: 'right', headerHalign: 'right' },   // Total TTC
                    6: { cellWidth: 28, halign: 'right', headerHalign: 'right' },   // Total HT
                    7: { cellWidth: 22, halign: 'right', headerHalign: 'right' },   // TVA
                },
                alternateRowStyles: { fillColor: [245, 245, 245] },
                margin: { left: 12, right: 12 },
                didDrawPage: (data) => {
                    const pageWidth = doc.internal.pageSize.getWidth();
                    const pageHeight = doc.internal.pageSize.getHeight();
                    const str = `${t('common.page') || 'Page'} ${doc.internal.getNumberOfPages()}`;
                    doc.setFontSize(8);
                    doc.text(str, pageWidth / 2, pageHeight - 6, { align: 'center' });
                },
            });


            // --------- Summary block
            const list = invoices || [];
            const sum = (arr, sel) => arr.reduce((s, it) => s + (sel(it) || 0), 0);
            const totalHT = sum(list, x => x.montantTotalHT);
            const totalTVA = sum(list, x => x.montantTotalTaxe);
            const totalTTC = sum(list, x => x.montantTotalTTC);
            const count = list.length;
            const avgTTC = count ? (totalTTC / count) : 0;

            // group by status
            const byStatus = list.reduce((acc, it) => {
                const k = it.statut || 'N/A';
                if (!acc[k]) acc[k] = { count: 0, ttc: 0 };
                acc[k].count += 1;
                acc[k].ttc += (it.montantTotalTTC || 0);
                return acc;
            }, {});

            let y = doc.lastAutoTable.finalY + 8;
            const boxW = pageWidth - margin * 2;
            doc.setFillColor(245, 245, 245);
            doc.roundedRect(margin, y, boxW, 40, 2, 2, 'F');
            doc.setDrawColor(220, 220, 220);
            doc.roundedRect(margin, y, boxW, 40, 2, 2, 'S');

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.setTextColor(30);
            doc.text(t('common.summary') || 'Résumé', margin + 4, y + 7);

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(60);

            // left col
            const leftX = margin + 4;
            const midX = margin + boxW / 2 + 4;
            let ly = y + 15;

            doc.text(`${t('common.count') || 'Nb factures'}: ${count}`, leftX, ly); ly += 6;
            doc.text(`${t('facture.table.totalHt') || 'Total HT'}: ${formatPrice(totalHT, false)}`, leftX, ly); ly += 6;
            doc.text(`${t('reception.totalTva') || 'TVA'}: ${formatPrice(totalTVA, false)}`, leftX, ly); ly += 6;
            doc.setFont('helvetica', 'bold');
            doc.text(`${t('facture.table.totalTtc') || 'Total TTC'}: ${formatPrice(totalTTC, false)}`, leftX, ly);

            // right col (status breakdown)
            doc.setFont('helvetica', 'normal');
            let ry = y + 15;
            doc.text(t('facture.table.status') || 'Statuts', midX, ry); ry += 5;
            Object.entries(byStatus).forEach(([st, v]) => {
                doc.text(`• ${st}: ${v.count} — ${formatPrice(v.ttc, false)}`, midX, ry);
                ry += 5;
            });

            // --------- Save / Open
            const filename = `Factures_${filters.dateFrom || ''}_${filters.dateTo || ''}.pdf`;
            const blob = doc.output('blob');
            const url = URL.createObjectURL(blob);
            const win = window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 1200);
            if (!win) doc.save(filename);
        } catch (e) {
            console.error('PDF export error', e);
            Swal.fire({
                icon: 'error',
                title: t('common.error') || 'Erreur',
                text: t('facture.pdfError') || 'Erreur lors de la génération du PDF.',
            });
        }
    };

    const handleAccuseFact = (invoice) => {
        Swal.fire({
            title: t('common.confirm'),
            text: t('bonLivraison.confirmAccuser'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('common.yes'),
            cancelButtonText: t('common.no'),
        }).then((result) => {
            if (!result.isConfirmed) return;
            let payload = {
                id: invoice.id,
                invoiceData: { ...invoice, accuse: true },
                onSuccess: () => {
                    Swal.fire({
                        icon: 'success',
                        title: t('common.updated'),
                        text: t('bonLivraison.updatedSuccess'),
                    });
                    let payload = {
                        ...filters,
                    };
                    dispatch(invoiceActions.listWithSubInvoices({
                        ...filters,
                    }));

                },
            };
            dispatch(invoiceActions.updateInvoiceRequest(payload));
        });
    }

    const handleDownloadListPdf = () => {
        try {
            const list = sortedInvoices || invoices || [];

            if (!list.length) {
                Swal.fire({
                    icon: 'warning',
                    title: t('common.warning') || 'Attention',
                    text: t('facture.noInvoices') || 'Aucune facture a exporter'
                });
                return;
            }

            const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 10;
            const right = pageWidth - margin;
            const selectedCompany = getSelectedCompanyModel();
            const selectedCompanyName = getCompanyValue(selectedCompany, ['name', 'businessName', 'raisonSociale'], company?.name || 'Matjar POS');
            const selectedCompanyAddress = getCompanyValue(selectedCompany, ['address', 'adresse', 'companyAddress'], '');
            const selectedCompanyIce = getCompanyValue(selectedCompany, ['ice', 'ICE', 'companyIce'], '');
            const selectedCompanyEmail = getCompanyValue(selectedCompany, ['email', 'companyEmail'], '');
            const selectedCompanyPhone = getCompanyValue(selectedCompany, ['phone1', 'telephone', 'phone', 'companyPhone'], '');
            const title = t('facture.list') || 'Liste des factures';
            const period = `${t('common.from') || 'Du'} ${filters.dateFrom || '-'} ${t('common.to') || 'au'} ${filters.dateTo || '-'}`;

            const drawHeader = () => {
                doc.setFillColor(23, 70, 162);
                doc.rect(0, 0, pageWidth, 14, 'F');
                doc.setTextColor(255);
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(10);
                doc.text(String(selectedCompanyName), margin, 9);

                doc.setTextColor(16, 24, 40);
                doc.setFontSize(16);
                doc.text(title, margin, 24);
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(9);
                doc.setTextColor(102, 112, 133);
                doc.text(period, margin, 31);

                const companyLines = [
                    selectedCompanyAddress,
                    selectedCompanyIce ? `ICE: ${selectedCompanyIce}` : '',
                    [selectedCompanyPhone, selectedCompanyEmail].filter(Boolean).join(' | ')
                ].filter(Boolean);

                doc.setFontSize(8);
                doc.setTextColor(71, 84, 103);
                companyLines.forEach((line, index) => {
                    const splitLines = doc.splitTextToSize(String(line), 112);
                    doc.text(splitLines, right, 22 + index * 5, { align: 'right' });
                });

                doc.setDrawColor(217, 224, 234);
                doc.line(margin, 38, right, 38);
            };

            drawHeader();

            const tableBody = list.map(inv => {
                const totalTTC = Number(inv.montantTotalTTC || inv.montantTTC || 0);
                const paidAmount = Number(inv.montantRegle || inv.montantPaye || 0);
                const remaining = Math.max(totalTTC - paidAmount, 0);

                return [
                    inv.numero || '',
                    inv.dateFacture || '',
                    inv.clientNom || '',
                    getInvoiceCompanyName(inv),
                    getPaymentModeLabel(inv.modeReglement || inv.modePaiement || inv.paymentMode),
                    inv.statut || '',
                    formatPrice(inv.montantTotalHT || 0, false),
                    formatPrice(inv.montantTotalTaxe || 0, false),
                    formatPrice(totalTTC, false),
                    formatPrice(paidAmount, false),
                    formatPrice(remaining, false),
                ];
            });

            autoTable(doc, {
                startY: 43,
                head: [[
                    t('facture.table.invoiceNb') || 'N Facture',
                    t('facture.table.Date') || 'Date',
                    t('facture.client') || 'Client',
                    t('company.company', 'Entreprise'),
                    t('facture.modeReglement', 'Mode reglement'),
                    t('facture.table.status') || 'Statut',
                    t('facture.table.totalHt') || 'Total HT',
                    t('reception.totalTva') || 'TVA',
                    t('facture.table.totalTtc') || 'Total TTC',
                    t('facture.montantRegle') || 'Regle',
                    t('reglement.resteAPayer', 'Reste'),
                ]],
                body: tableBody,
                styles: {
                    font: 'helvetica',
                    fontSize: 7.4,
                    cellPadding: 2.1,
                    textColor: [52, 64, 84],
                    lineColor: [234, 236, 240],
                    lineWidth: 0.1,
                    overflow: 'linebreak',
                },
                headStyles: {
                    fillColor: [23, 70, 162],
                    textColor: 255,
                    fontStyle: 'bold',
                    halign: 'center',
                    valign: 'middle',
                },
                alternateRowStyles: { fillColor: [248, 250, 252] },
                columnStyles: {
                    0: { cellWidth: 27, halign: 'center' },
                    1: { cellWidth: 21, halign: 'center' },
                    2: { cellWidth: 39, halign: 'left' },
                    3: { cellWidth: 31, halign: 'left' },
                    4: { cellWidth: 27, halign: 'center' },
                    5: { cellWidth: 22, halign: 'center' },
                    6: { cellWidth: 22, halign: 'right' },
                    7: { cellWidth: 20, halign: 'right' },
                    8: { cellWidth: 24, halign: 'right', fontStyle: 'bold' },
                    9: { cellWidth: 22, halign: 'right' },
                    10: { cellWidth: 22, halign: 'right' },
                },
                margin: { left: margin, right: margin, top: 43, bottom: 13 },
                didDrawPage: () => {
                    if (doc.internal.getCurrentPageInfo().pageNumber > 1) {
                        drawHeader();
                    }

                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(8);
                    doc.setTextColor(102, 112, 133);
                    doc.text(
                        `${t('common.page') || 'Page'} ${doc.internal.getNumberOfPages()}`,
                        pageWidth / 2,
                        pageHeight - 6,
                        { align: 'center' }
                    );
                },
            });

            const sum = (arr, sel) => arr.reduce((s, it) => s + Number(sel(it) || 0), 0);
            const totalHT = sum(list, x => x.montantTotalHT);
            const totalTVA = sum(list, x => x.montantTotalTaxe);
            const totalTTC = sum(list, x => x.montantTotalTTC || x.montantTTC);
            const totalPaid = sum(list, x => x.montantRegle || x.montantPaye);
            const totalRemaining = Math.max(totalTTC - totalPaid, 0);
            const count = list.length;
            const byStatus = list.reduce((acc, it) => {
                const key = it.statut || 'N/A';
                if (!acc[key]) acc[key] = { count: 0, ttc: 0 };
                acc[key].count += 1;
                acc[key].ttc += Number(it.montantTotalTTC || it.montantTTC || 0);
                return acc;
            }, {});

            let y = doc.lastAutoTable.finalY + 8;
            if (y > pageHeight - 48) {
                doc.addPage();
                drawHeader();
                y = 46;
            }

            const boxW = pageWidth - margin * 2;
            doc.setFillColor(248, 250, 252);
            doc.setDrawColor(217, 224, 234);
            doc.roundedRect(margin, y, boxW, 38, 2, 2, 'FD');

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.setTextColor(16, 24, 40);
            doc.text(t('common.summary') || 'Resume', margin + 4, y + 7);

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8.5);
            doc.setTextColor(71, 84, 103);

            const leftX = margin + 4;
            const midX = margin + 92;
            const rightX = margin + 186;
            let ly = y + 15;
            doc.text(`${t('common.count') || 'Nb factures'}: ${count}`, leftX, ly); ly += 5;
            doc.text(`${t('facture.table.totalHt') || 'Total HT'}: ${formatPrice(totalHT, false)}`, leftX, ly); ly += 5;
            doc.text(`${t('reception.totalTva') || 'TVA'}: ${formatPrice(totalTVA, false)}`, leftX, ly);

            let my = y + 15;
            doc.text(`${t('facture.table.totalTtc') || 'Total TTC'}: ${formatPrice(totalTTC, false)}`, midX, my); my += 5;
            doc.text(`${t('facture.montantRegle') || 'Regle'}: ${formatPrice(totalPaid, false)}`, midX, my); my += 5;
            doc.setFont('helvetica', 'bold');
            doc.text(`${t('reglement.resteAPayer', 'Reste')}: ${formatPrice(totalRemaining, false)}`, midX, my);

            doc.setFont('helvetica', 'normal');
            let ry = y + 15;
            doc.text(t('facture.table.status') || 'Statuts', rightX, ry); ry += 5;
            Object.entries(byStatus).forEach(([status, value]) => {
                doc.text(`- ${status}: ${value.count} (${formatPrice(value.ttc, false)})`, rightX, ry);
                ry += 5;
            });

            const filename = `Factures_${filters.dateFrom || ''}_${filters.dateTo || ''}.pdf`;
            const blob = doc.output('blob');
            const url = URL.createObjectURL(blob);
            const win = window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 1200);
            if (!win) doc.save(filename);
        } catch (e) {
            console.error('PDF export error', e);
            Swal.fire({
                icon: 'error',
                title: t('common.error') || 'Erreur',
                text: t('facture.pdfError') || 'Erreur lors de la generation du PDF.',
            });
        }
    };

    const handleAnnulerFacture = (facture) => {
        Swal.fire({
            title: t('facture.deleteConfirmTitle'),
            text: t('facture.deleteConfirmText'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('common.confirm'),
            cancelButtonText: t('common.cancel')
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    let payload = {
                        id: facture.id,
                        motifAnnulation: "",
                        onSuccess: () => {
                            Swal.fire(t('facture.deleted'), '', 'success');
                            debouncedSearch();
                        },
                        onError: (error) => {
                            Swal.fire({
                                icon: 'error',
                                title: t('delivery.deleteError'),
                                text: t('delivery.deleteErrorMessage')
                            });
                        }
                    }
                    dispatch(invoiceActions.cancelInvoiceRequest(payload));

                } catch (error) {
                    Swal.fire({
                        icon: 'error',
                        title: t('common.error'),
                        text: t('delivery.deleteError')
                    });
                }
            }
        });
    }


    return (
        <Container fluid className="enterprise-page document-enterprise-page">
            {/* En-tête avec statistiques */}
            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaFileInvoice className="text-primary me-2" />
                                {t('facture.list')}
                            </h4>
                            <div className="d-flex flex-wrap align-items-end gap-2">
                                <div style={{ minWidth: '260px' }}>
                                    <Label htmlFor="companyIdHeader" className="mb-1 small">{t('company.company', 'Entreprise')}:</Label>
                                    <Select
                                        id="companyIdHeader"
                                        options={companyOptionsWithAll}
                                        value={companyOptionsWithAll.find(option => option.value === filters.companyId)}
                                        onChange={(selectedOption) => {
                                            setFilters(f => ({ ...f, companyId: selectedOption ? selectedOption.value : null }));
                                        }}
                                        placeholder={t('company.selectCompany', 'Selectionner une entreprise')}
                                        isSearchable
                                        menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                        styles={compactSelectStyles}
                                    />
                                </div>

                                <Button
                                    color="primary"
                                    onClick={handleDownloadListPdf}
                                    size="sm"
                                    className="d-flex align-items-center"
                                >
                                    <FaFilePdf className="me-2" size={14} />
                                    {t('facture.downloadListPdf') || 'Export PDF'}
                                </Button>

                                <Button
                                    color="success"
                                    onClick={handleExportInvoicesExcel}
                                    size="sm"
                                    className="d-flex align-items-center"
                                    title={t('facture.exportExcel') || 'Exporter Excel'}
                                >
                                    <FaFileExcel className="me-2" size={14} />
                                    Excel
                                </Button>
                            </div>

                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Card className="shadow-sm mb-4">
                <CardBody>
                    <Row className="mb-3 g-2 align-items-end">
                        <Col lg={2} md={3} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label className="mb-1 small">{t('reglement.search')}</Label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light py-1 px-2">
                                        <FaSearch />
                                    </span>
                                    <Input
                                        type="text"
                                        innerRef={searchInputRef}
                                        placeholder={t('facture.searchPlaceholder')}
                                        value={filters.query}
                                        onChange={e => {
                                            const value = e.target.value.toUpperCase();
                                            setFilters(prevFilters => ({
                                                ...prevFilters,
                                                query: value
                                            }));
                                        }}
                                        className="text-uppercase form-control-sm"
                                        style={{ textTransform: 'uppercase' }}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                debouncedSearch(e.target.value.toUpperCase());
                                            }
                                        }}
                                    />
                                </div>
                            </FormGroup>
                        </Col>
                        <Col lg={1} md={2} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label className="mb-1 small">{t('common.amount', 'Montant')}</Label>
                                <Input
                                    type="number"
                                    value={filters.amount}
                                    min="0"
                                    step="0.01"
                                    bsSize="sm"
                                    placeholder={t('common.amount', 'Montant')}
                                    onChange={e => setFilters(f => ({ ...f, amount: e.target.value }))}
                                />
                            </FormGroup>
                        </Col>
                        <Col lg={2} md={2}>
                            <FormGroup className="mb-0">
                                <Label htmlFor="clientId" className="mb-1 small">{t('bonLivraison.client')}:</Label>
                                <Select
                                    id="clientId"
                                    options={clientOptionsWithAll}
                                    value={clientOptionsWithAll.find(option => option.value === filters.clientId)}
                                    onChange={(selectedOption) => {
                                        setFilters(f => ({ ...f, clientId: selectedOption ? selectedOption.value : null }));
                                    }}
                                    placeholder={t('bonLivraison.selectClient')}
                                    isSearchable
                                    menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                    styles={compactSelectStyles}
                                />
                            </FormGroup>
                        </Col>
                        <Col lg={2} md={2} className="d-none">
                            <FormGroup className="mb-0 d-none">
                                <Label htmlFor="companyId" className="mb-1 small d-none">{t('company.company', 'Entreprise')}:</Label>
                                <Select
                                    id="companyId"
                                    options={companyOptionsWithAll}
                                    value={companyOptionsWithAll.find(option => option.value === filters.companyId)}
                                    onChange={(selectedOption) => {
                                        setFilters(f => ({ ...f, companyId: selectedOption ? selectedOption.value : null }));
                                    }}
                                    placeholder={t('company.selectCompany', 'Sélectionner une entreprise')}
                                    isSearchable
                                    menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                    styles={compactSelectStyles}
                                />
                            </FormGroup>
                        </Col>
                        <Col lg={1} md={2} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label for="status" className="mb-1 small">{t('facture.table.status', 'Statut')}</Label>
                                <Input
                                    type="select"
                                    id="status"
                                    value={filters.status}
                                    bsSize="sm"
                                    onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
                                >
                                    <option value="">{t('common.all', 'Tous')}</option>
                                    <option value="IMPAYE">{t('facture.unpaid', 'Impayée')}</option>
                                    <option value="PAYEE">{t('facture.paid', 'Payée')}</option>
                                    <option value="ANNULEE">{t('facture.cancelled', 'Annulée')}</option>
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col xl={1} lg={2} md={6}>
                            <FormGroup className="mb-0">
                                <Label className="mb-1 small">{t('delivery.accuse', 'Accusé')}</Label>
                                <Input
                                    type="select"
                                    value={filters.accuse}
                                    bsSize="sm"
                                    onChange={e =>
                                        setFilters(f => ({ ...f, accuse: Number(e.target.value) }))
                                    }
                                >
                                    {accuseOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col lg={1} md={2} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label for="month" className="mb-1 small">{t('common.month', 'Mois')}</Label>
                                <Input
                                    type="month"
                                    id="month"
                                    value={filters.month}
                                    bsSize="sm"
                                    onChange={e => {
                                        const month = e.target.value;
                                        const { dateFrom, dateTo } = getMonthDateRange(month);
                                        setFilters(f => ({
                                            ...f,
                                            month,
                                            dateFrom: dateFrom || f.dateFrom,
                                            dateTo: dateTo || f.dateTo,
                                        }));
                                    }}
                                />
                            </FormGroup>
                        </Col>
                        <Col lg={1} md={2} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label for="dateFrom" className="mb-1">Date début</Label>
                                <Input
                                    type="date"
                                    id="dateFrom"
                                    value={filters.dateFrom}
                                    bsSize="sm"
                                    onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value, month: '' }))}
                                />
                            </FormGroup>
                        </Col>
                        <Col lg={1} md={2} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label for="dateTo" className="mb-1">Date fin</Label>
                                <Input
                                    type="date"
                                    id="dateTo"
                                    value={filters.dateTo}
                                    bsSize="sm"
                                    onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value, month: '' }))}
                                />
                            </FormGroup>
                        </Col>
                        <Col lg={1} md={1} sm={6} xs={12} className="d-flex align-items-end">
                            <Button
                                color="secondary"
                                onClick={clearFilters}
                                title={t('common.clearFilters')}
                                className="mb-3"
                                style={{ minHeight: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                <FaFilter />
                            </Button>
                        </Col>
                        <Col lg={1} md={2} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label className="mb-1 small">{t('common.lines', 'Lignes')}</Label>
                                <Input
                                    type="select"
                                    value={rowsPerPage}
                                    bsSize="sm"
                                    onChange={e => setRowsPerPage(Number(e.target.value))}
                                >
                                    {[10, 20, 50, 100].map((size) => (
                                        <option key={size} value={size}>{size}</option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* Tableau des factures */}
            <Card className="shadow-sm">
                <CardBody>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <div className="mt-2 text-muted">{t('common.loading')}</div>
                        </div>
                    ) : (
                        <>
                            <div className="enterprise-table-scroll">
                                <table className="table table-hover table-striped table-bordered document-grouped-table" style={{ fontSize: '0.8rem' }}>
                                    <thead className="thead-light">
                                        <tr>
                                            {renderSortableHeader(t('facture.table.invoiceNb'), 'numero')}
                                            {renderSortableHeader(t('facture.client'), 'clientNom')}
                                            <th className="align-middle text-center">{t('facture.table.amounts', 'Montants')}</th>
                                            <th className="align-middle text-center">{t('facture.table.blNumbers')}</th>
                                            <th className="align-middle text-center">{t('facture.table.status')}</th>
                                            <th className="align-middle text-center" style={{ width: '150px' }}>{t('facture.table.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedInvoices.map((invoice) => {
                                            const remainingAmount = getInvoiceRemainingAmount(invoice);
                                            const isPaid = isInvoicePaid(invoice);
                                            const isUnpaid = invoice.statut !== 'ANNULEE' && !isPaid;
                                            return (
                                                <tr key={invoice.id} className={isPaid ? 'table-success' : isUnpaid ? 'table-danger' : ''}>
                                                    <td className="align-middle py-1">
                                                        <div className="document-main-ref">{invoice.numero}</div>
                                                        {isPaid && (
                                                            <Badge color="success" pill className="mt-1">
                                                                {t('facture.paid', 'Payée')}
                                                            </Badge>
                                                        )}
                                                        {isUnpaid && (
                                                            <Badge color="danger" pill className="mt-1">
                                                                {t('facture.unpaid', 'Impayée')}
                                                            </Badge>
                                                        )}
                                                        <div className="document-subtext">{t('facture.table.Date')}: {invoice.dateFacture || 'N/A'}</div>
                                                        <div className="document-subtext">{t('common.user')}: {invoice.createdBy || 'N/A'}</div>
                                                        <div className="document-subtext">{t('company.company', 'Entreprise')}: {getInvoiceCompanyName(invoice) || '-'}</div>
                                                    </td>
                                                    <td className="align-middle py-1">
                                                        <div className="document-strong">{invoice?.clientNom || 'N/A'}</div>
                                                    </td>
                                                    <td className="align-middle">
                                                        <div className="document-amount-grid">
                                                            <span>TTC</span><strong>{formatPrice(invoice.montantTotalTTC, false)}</strong>
                                                            <span>HT</span><span>{formatPrice(invoice.montantTotalHT, false)}</span>
                                                            <span>TVA</span><span>{formatPrice(invoice.montantTotalTaxe, false)}</span>
                                                            <span>{t('facture.montantRegle')}</span><span>{formatPrice(invoice.montantRegle, false)}</span>
                                                            <span>{t('reglement.resteAPayer', 'Reste')}</span><strong>{formatPrice(remainingAmount, false)}</strong>
                                                        </div>
                                                    </td>


                                                    <td className="align-middle text-center">
                                                        {invoice.bonLivraionsRefs ? (
                                                            <div className="document-docs justify-content-center">
                                                                {invoice.bonLivraionsRefs.split(',').map((receipt, index) => (
                                                                    <Badge key={index} color="success" className='mb-1' style={{ fontSize: '10px' }}>
                                                                        {receipt.trim()}
                                                                    </Badge>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted">Aucun</span>
                                                        )}
                                                    </td>
                                                    <td className="align-middle text-center">
                                                        {getStatusBadge(invoice.statut)}
                                                        <div className="document-subtext mt-1">
                                                            {t('delivery.accuse')}: {invoice.accuse ? t('common.yes', 'Oui') : t('common.no', 'Non')}
                                                        </div>
                                                    </td>

                                                    <td className="align-middle text-center">
                                                        <div className="btn-group">
                                                            {/* Bouton PDF */}
                                                            <Button
                                                                color="secondary"
                                                                title={t('facture.downloadPdf')}
                                                                onClick={() => handleGeneratePdf(invoice)}
                                                                size="sm"
                                                                className="me-1"
                                                            >
                                                                <FaFilePdf size={12} />
                                                            </Button>

                                                            {/* Bouton Éditer */}
                                                            <Button
                                                                color="warning"
                                                                title={t('facture.edit')}
                                                                onClick={() => handleEditInvoice(invoice)}
                                                                size="sm"
                                                                className="me-1"
                                                            //disabled={!canEditInvoice(invoice)}
                                                            >
                                                                <FaEdit size={12} />
                                                            </Button>
                                                            {invoice.statut !== 'ANNULEE' && !invoice.accuse && <Button
                                                                color="success"
                                                                title={t('bonLivraison.accuser')}
                                                                onClick={() => handleAccuseFact(invoice)}
                                                                className='me-1'
                                                                size="sm"
                                                            >
                                                                <FaCheckCircle />
                                                            </Button>}
                                                            {remainingAmount > 0 && invoice.statut !== 'ANNULEE' && (
                                                                <Button
                                                                    color="success"
                                                                    title={t('reglement.pay', 'Regler')}
                                                                    onClick={() => handleCreatePaymentFromInvoice(invoice)}
                                                                    className='me-1'
                                                                    size="sm"
                                                                >
                                                                    <FaMoneyBillWave size={12} />
                                                                </Button>
                                                            )}

                                                            {invoice.statut !== 'ANNULEE' && <Button
                                                                color="danger"
                                                                title={t('commande.cancel')}
                                                                onClick={() => handleAnnulerFacture(invoice)}
                                                                className='me-1'
                                                                size="sm"
                                                            >
                                                                <FaTrash />
                                                            </Button>}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {(!invoices || invoices.length === 0) && (
                                            <tr>
                                                <td colSpan="6" className="text-center py-4">
                                                    <div className="text-muted">{t('facture.noInvoices')}</div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                    <tfoot>
                                        <tr className="bg-light fw-bold">
                                            <td colSpan="2" className="text-end">{t('common.totals')}</td>
                                            <td className="align-middle">
                                                <div className="document-amount-grid">
                                                    <span>TTC</span><strong>{formatPrice(invoices.reduce((sum, invoice) => sum + (invoice.montantTotalTTC || 0), 0), false)}</strong>
                                                    <span>HT</span><span>{formatPrice(invoices.reduce((sum, invoice) => sum + (invoice.montantTotalHT || 0), 0), false)}</span>
                                                    <span>TVA</span><span>{formatPrice(invoices.reduce((sum, invoice) => sum + (invoice.montantTotalTaxe || 0), 0), false)}</span>
                                                    <span>{t('facture.montantRegle')}</span><span>{formatPrice(invoices.reduce((acc, invoice) => acc + invoice.montantRegle, 0), false)}</span>
                                                </div>
                                            </td>
                                            <td colSpan="3"></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-3">
                                <div className="text-muted small">
                                    {t('common.paginationInfo', {
                                        defaultValue: '{{start}}-{{end}} sur {{total}}',
                                        start: pageStart,
                                        end: pageEnd,
                                        total: sortedInvoices.length,
                                    })}
                                </div>
                                <Pagination size="sm" className="mb-0">
                                    <PaginationItem disabled={currentPageSafe === 1}>
                                        <PaginationLink first onClick={() => handlePageChange(1)} />
                                    </PaginationItem>
                                    <PaginationItem disabled={currentPageSafe === 1}>
                                        <PaginationLink previous onClick={() => handlePageChange(currentPageSafe - 1)} />
                                    </PaginationItem>
                                    <PaginationItem disabled>
                                        <PaginationLink>{currentPageSafe} / {totalPages}</PaginationLink>
                                    </PaginationItem>
                                    <PaginationItem disabled={currentPageSafe === totalPages}>
                                        <PaginationLink next onClick={() => handlePageChange(currentPageSafe + 1)} />
                                    </PaginationItem>
                                    <PaginationItem disabled={currentPageSafe === totalPages}>
                                        <PaginationLink last onClick={() => handlePageChange(totalPages)} />
                                    </PaginationItem>
                                </Pagination>
                            </div>
                        </>
                    )}
                </CardBody>
            </Card>

            {/* Modal pour la gestion des sous-factures */}
            <Modal
                isOpen={showSubInvoiceModal}
                toggle={() => setShowSubInvoiceModal(false)}
                size="xl"
                className="modal-fullscreen-lg-down"
            >
                <ModalHeader toggle={() => setShowSubInvoiceModal(false)}>
                    {t('subInvoice.manageSubInvoices')} - {selectedInvoiceForSubInvoices?.numero}
                </ModalHeader>
                <ModalBody>
                    {selectedInvoiceForSubInvoices && (
                        <SubInvoiceManager
                            parentInvoice={selectedInvoiceForSubInvoices}
                            existingSubInvoices={getSubInvoicesForInvoice(selectedInvoiceForSubInvoices.id)}
                            onSubInvoicesCreated={handleSubInvoicesCreation}
                            onClose={() => setShowSubInvoiceModal(false)}
                        />
                    )}
                </ModalBody>
            </Modal>

            {/* Modal pour l'édition des factures */}
            <Modal
                isOpen={showEditModal}
                toggle={handleCloseEditModal}
                size="xl"
                className="modal-fullscreen-lg-down"
                style={{ maxWidth: '95%', width: '95%', margin: '10px auto' }}
            >
                <ModalHeader toggle={handleCloseEditModal}>
                    {t('facture.editInvoice')} - {selectedInvoiceForEdit?.numero}
                </ModalHeader>
                <ModalBody>
                    {selectedInvoiceForEdit && (
                        <ViewFacture
                            invoice={selectedInvoiceForEdit}
                            onSave={handleSaveInvoice}
                            onCancel={handleCloseEditModal}
                            isEditing={true}
                        />
                    )}
                </ModalBody>
            </Modal>
            <Modal isOpen={showInvoiceModal} toggle={handleCloseInvoiceModal} size="xl"
                className="modal-fullscreen-lg-down"
                style={{ maxWidth: '95%', width: '95%', margin: '10px auto' }}>
                <ModalHeader toggle={handleCloseInvoiceModal}>
                    {t('facture.newInvoice')}
                </ModalHeader>
                <ModalBody>
                    <InvoiceForm
                        initialValues={invoice}
                        onClose={handleCloseInvoiceModal}
                    />
                </ModalBody>
            </Modal>
        </Container>
    );
};

export default InvoiceList;


